/**
 * INV.PRESERVE guarantee (I2) — F-coalgebra structure.
 *
 * Neutralizes time reversal (G3) by ensuring that every state transition
 * preserves established invariants. If a state is safe, the next state is safe.
 *
 * Agda source: Kernel/INV/PRESERVE/Neutralization.agda
 * Realization fidelity: STRUCTURAL (transition guards enforced; coalgebra laws documented)
 * Safety class: Parametric (acquired immunity)
 * Neutralization mode: INERT — violations exist but cannot compose into exploits
 * Tier: 2 (structural-geometric)
 * Legacy name: TEMP
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Guarantee } from "../foundation/types.js";
import { INV_PRESERVE_META } from "../shared/theorem-meta.js";
export { INV_PRESERVE_META };
import type { Invariant, EstablishedState } from "./invEstablish.js";

// ── Structure: F-coalgebra ─────────────────────────────────────

/** A guarded state transition — only succeeds if invariants are preserved. */
export interface GuardedTransition<S> {
  readonly name: string;
  readonly transition: (state: S) => S;
}

/**
 * Apply a transition to an established state, verifying invariants are preserved.
 * Returns a new EstablishedState or throws if any invariant is broken.
 */
export function preservingTransition<S>(
  state: EstablishedState<S>,
  transition: GuardedTransition<S>,
  invariants: readonly Invariant<S>[],
): EstablishedState<S> {
  const nextState = transition.transition(state);
  for (const inv of invariants) {
    if (!inv.check(nextState)) {
      throw new Error(
        `INV.PRESERVE: transition "${transition.name}" broke invariant "${inv.name}" — ` +
          "state rolled back to preserve safety",
      );
    }
  }
  return nextState as EstablishedState<S>;
}

export interface InvPreserveViolation {
  readonly __brand: "InvPreserveViolation";
  readonly kind: "broken-invariant";
}

export interface InvPreserveStructure<S> {
  readonly invariants: readonly Invariant<S>[];
  readonly transitions: readonly GuardedTransition<S>[];
}

export function createInvPreserveGuarantee<S>(
  structure: InvPreserveStructure<S>,
): Guarantee<InvPreserveStructure<S>, InvPreserveViolation> {
  return {
    structure,
    neutralize: (_violation: InvPreserveViolation): never => {
      throw new Error(
        "INV.PRESERVE violation is structurally inert: " +
          "preservingTransition() enforces invariants on every state change",
      );
    },
    meta: INV_PRESERVE_META,
  };
}
